import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaClock, FaBolt, FaChartLine, FaUsers } from "react-icons/fa";
import TopStats from "../components/TopStats";
import TopSelectedPlayers from "../components/TopSelectedPlayers";
import TopTransfersIn from "../components/TopTransfersIn";
import TopTransfersOut from "../components/TopTransfersOut";
import InjuryNews from "../components/InjuryNews";
import DreamTeam from "../components/DreamTeam";
import Suggestion from "../components/Suggestion";
import Live from "../components/Live";
import PriceChangePrediction from "../components/PriceChangePrediction";
import AdComponent from "../components/AdComponent";
import { fetchFPLData } from "../api/fplApi";

const Section = ({ eyebrow, title, action, children, className = "" }) => (
  <section className={`fpl-panel ${className}`}>
    <div className="fpl-section-head">
      <div>
        {eyebrow && <div className="fpl-eyebrow">{eyebrow}</div>}
        <h2>{title}</h2>
      </div>
      {action && <div className="fpl-section-action">{action}</div>}
    </div>
    {children}
  </section>
);

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    fetchFPLData().then((data) => {
      if (!data) return;
      setEvents(data.events || []);
      setPlayers(data.elements || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const targetGw = useMemo(() => events.find((e) => e.is_next) || events.find((e) => e.is_current) || events[0], [events]);
  const deadline = targetGw?.deadline_time ? new Date(targetGw.deadline_time) : null;
  const countdown = useMemo(() => {
    if (!deadline) return "Season live";
    const diff = Math.max(0, deadline.getTime() - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return days > 0 ? `${days}d ${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m` : `${hours}h ${String(mins).padStart(2, "0")}m`;
  }, [deadline, now]);

  const heroPlayers = useMemo(() => [...players]
    .sort((a, b) => Number(b.selected_by_percent) - Number(a.selected_by_percent))
    .slice(0, 3), [players]);

  return (
    <div className="fpl-dashboard w-full">
      <section className="fpl-season-hero">
        <div className="fpl-hero-copy">
          <div className="fpl-hero-kicker">FPL Nepal · Season 16</div>
          <div className="fpl-hero-title-row">
            <h2>FPL Nepal</h2>
            <span className="fpl-hero-season-pill">2026/27</span>
          </div>
          <p>Official FPL stats, fixtures, trends and Gameweek essentials<br className="hidden xl:block" /> in one place.</p>
          <div className="fpl-hero-meta">
            <span><FaBolt /> Official FPL data</span>
            <span><FaChartLine /> Live season trends</span>
            <span><FaUsers /> Community hub</span>
          </div>
        </div>

        <div className="fpl-hero-players" aria-hidden="true">
          {heroPlayers.map((p, i) => (
            <img
              key={p.id}
              className={`fpl-hero-player player-${i + 1}`}
              src={`https://resources.premierleague.com/premierleague25/photos/players/110x140/${p.code}.png`}
              alt=""
            />
          ))}
        </div>

        <div className="fpl-hero-deadline">
          <div className="fpl-hero-deadline-icon"><FaClock /></div>
          <div>
            <small>{targetGw ? `GW${targetGw.id} Deadline` : "FPL Season"}</small>
            <strong>{countdown}</strong>
            <span>{deadline ? deadline.toLocaleString(undefined, { weekday:"short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Official FPL"}</span>
          </div>
        </div>
      </section>

      <div className="fpl-home-grid">
        <div className="fpl-home-left">
          <Section eyebrow="Season leaders" title="Top Stats" action={<Link to="/players">View all</Link>}>
            <TopStats />
          </Section>

          <Section eyebrow="Market watch" title="FPL Market" action={<Link to="/price-change-prediction">View all</Link>}>
            <div className="fpl-market-grid">
              <PriceChangePrediction compact />
              <TopTransfersIn compact />
              <TopTransfersOut compact />
            </div>
          </Section>
        </div>

        <Section eyebrow="Match centre" title="Fixtures & Results" action="Official FPL" className="fpl-fixtures-panel">
          <Live compact />
        </Section>
      </div>

      <Section eyebrow="Ownership" title="Most Selected Players" action="Overall FPL ownership" className="fpl-ownership-panel">
        <TopSelectedPlayers />
      </Section>

      <div className="fpl-bottom-grid">
        <Section eyebrow="Upcoming Gameweek" title={`Top Picks${targetGw ? ` for GW${Math.min(38, targetGw.id)}` : ""}`} action={<Link to="/suggestion">View all</Link>}>
          <Suggestion compact />
        </Section>

        <Section eyebrow="Completed Gameweek" title="Team of the Week" action={<Link to="/dream-team">View all</Link>}>
          <DreamTeam compact />
        </Section>

        <Section eyebrow="Availability" title="Injuries & Suspensions" action={<Link to="/injuries">View all</Link>}>
          <InjuryNews compact />
        </Section>
      </div>

      <div className="pt-1"><AdComponent /></div>
    </div>
  );
};

export default Dashboard;
